import { useCallback, useContext, useEffect, useRef } from 'react'

import { HubSpotSettings } from '../api'
import { useSessionLanguage } from '../global/context/SessionSettingsContext'
import { useExternalScripts } from '../hooks/useExternalScripts'
import { getClientPrimaryEmail } from './AccountAccess/accountEmailStatuses'
import { useAccountReadContext } from './AccountContextContext'
import { useApiClient } from './ApiClient'
import { AuthSessionContext } from './AuthContext'
import { ClientApiClient } from './clientApi'
import { useLocallyPersistedState } from './useStorage'

const HUBSPOT_LOADED_EVENT = 'hubspot:loaded'

const createLogger = (prefix: string) => ({
  log: (...args: any[]) => console.log(`[HubSpot][${prefix}]`, ...args),
  error: (...args: any[]) => console.error(`[HubSpot][${prefix}]`, ...args),
  warn: (...args: any[]) => console.warn(`[HubSpot][${prefix}]`, ...args),
  info: (...args: any[]) => console.info(`[HubSpot][${prefix}]`, ...args),
})

const logger = createLogger('HS')

export const setHubSpotSettings = (settings: HubSpotSettings): void => {
  logger.log('Setting HubSpot settings', {
    ...settings,
    tokenLength: settings.identificationToken?.length,
  })
  window.hsConversationsSettings = {
    ...settings,
    loadImmediately: false,
  }
}

export const getHubSpotWidgetStatus = (): { loaded: boolean } | undefined => {
  const status = window.HubSpotConversations?.widget?.status?.()
  logger.log('HubSpot widget status:', status)
  return status
}

export const loadHubSpotWidget = (): void => {
  logger.log('attempting to load HubSpot widget')
  if (window.HubSpotConversations?.widget?.load) {
    window.HubSpotConversations.widget.load()
    logger.log('hubSpot widget load method called')
  } else {
    logger.error('cannot load HubSpot widget - HubSpotConversations.widget.load not available')
  }
}

export const resetHubSpotWidget = (): void => {
  logger.log('attempting to reset HubSpot widget')
  if (window.HubSpotConversations?.clear) {
    window.HubSpotConversations.clear({ resetWidget: true })
    logger.log('hubSpot widget reset completed')
  } else {
    logger.warn('cannot reset HubSpot widget - HubSpotConversations.clear not available')
  }
}

const isHubSpotLoaded = (): boolean => {
  const isLoaded = !!(
    window.HubSpotConversations &&
    window.HubSpotConversations.widget &&
    typeof window.HubSpotConversations.widget.load === 'function'
  )
  logger.log('HubSpot loaded check:', isLoaded)
  return isLoaded
}

export const useHubSpot = (): void => {
  const { account } = useAccountReadContext()
  const locale = useSessionLanguage()
  const [auth] = useContext(AuthSessionContext)
  const apiClient = useApiClient(ClientApiClient)
  const hubspotLogger = createLogger('useHubSpot')

  // Ref to track initialization status
  const initializationState = useRef({
    scriptLoaded: false,
    initAttempted: false,
    lastTokenFetch: 0,
    initializationInProgress: false,
  })

  const [hubSpotIdentificationToken, setHubSpotIdentificationToken] = useLocallyPersistedState<
    string | undefined
  >('hubSpotIdentificationToken', undefined)

  const scriptLoadCallback = useCallback(() => {
    hubspotLogger.log('HubSpot script loading callback triggered')

    // let's check if HS is actually available
    if (window.HubSpotConversations) {
      hubspotLogger.log('HubSpot script loaded successfully')
      initializationState.current.scriptLoaded = true

      const loadEvent = new Event(HUBSPOT_LOADED_EVENT)
      window.dispatchEvent(loadEvent)
    } else {
      hubspotLogger.error('HS script callback triggered but HubSpotConversations not available')
      setTimeout(() => {
        if (window.HubSpotConversations) {
          hubspotLogger.log('HubSpot script loaded after delay')
          initializationState.current.scriptLoaded = true
          const loadEvent = new Event(HUBSPOT_LOADED_EVENT)
          window.dispatchEvent(loadEvent)
        } else {
          hubspotLogger.error('HS script loading failed even after delay')
        }
      }, 2000)
    }
  }, [])

  useExternalScripts('https://js.hs-scripts.com/9294264.js', 'hs-script-loader', scriptLoadCallback)

  // Function to fetch a new HubSpot identification token
  const fetchIdentificationToken = useCallback(async () => {
    hubspotLogger.log('Fetching new HubSpot identification token')
    try {
      const response = await apiClient.getHBToken()
      const token = response?.token

      if (!token) {
        hubspotLogger.error('API returned empty token')
        return undefined
      }

      hubspotLogger.log('Successfully fetched new token', { tokenLength: token.length })
      initializationState.current.lastTokenFetch = Date.now()
      setHubSpotIdentificationToken(token)
      return token
    } catch (error: unknown) {
      hubspotLogger.error('Error fetching identification token:', error)
      return undefined
    }
  }, [apiClient, setHubSpotIdentificationToken])

  // Main function to initialize HubSpot
  const initializeHubSpot = useCallback(async () => {
    const initLogger = createLogger('initialize')

    // Prevent concurrent initialization
    if (initializationState.current.initializationInProgress) {
      initLogger.log('Initialization already in progress, skipping')
      return
    }

    initializationState.current.initializationInProgress = true
    initializationState.current.initAttempted = true

    try {
      // Check if account exists
      if (!account) {
        initLogger.log('No account available, resetting widget')
        resetHubSpotWidget()
        return
      }

      initLogger.log('Account found', {
        accountId: account.id,
        accountType: account.type,
      })

      // Get primary email
      const email = getClientPrimaryEmail(account)
      if (!email) {
        initLogger.error('No primary email found for account', { accountId: account.id })
        return
      }

      initLogger.log('Found primary email', { email })

      // Token handling
      let identificationToken = hubSpotIdentificationToken
      const tokenAge = Date.now() - initializationState.current.lastTokenFetch

      // Fetch new token if none exists or it's older than 12 hours
      if (!identificationToken || tokenAge > 12 * 60 * 60 * 1000) {
        initLogger.log('Token missing or expired, fetching new one', {
          hasToken: !!identificationToken,
          tokenAge: tokenAge / (60 * 60 * 1000) + ' hours',
        })

        identificationToken = await fetchIdentificationToken()
        if (!identificationToken) {
          initLogger.error('Failed to get identification token, aborting initialization')
          return
        }
      } else {
        initLogger.log('Using existing token', {
          tokenLength: identificationToken.length,
          tokenAge: tokenAge / (60 * 60 * 1000) + ' hours',
        })
      }

      // Reset the widget first to clear any previous state
      resetHubSpotWidget()

      // Small delay to ensure reset completes
      await new Promise((resolve) => setTimeout(resolve, 500))

      // Configure HubSpot with user information
      initLogger.log('Setting up HubSpot with user information', {
        email,
        tokenLength: identificationToken.length,
        locale,
      })

      setHubSpotSettings({
        identificationEmail: email,
        identificationToken: identificationToken,
      })

      // Load the widget
      if (!window.HubSpotConversations) {
        initLogger.log('HubSpotConversations not available, setting up onReady callback')
        window.hsConversationsOnReady = [
          () => {
            initLogger.log('HubSpot onReady callback triggered')
            loadHubSpotWidget()
          },
        ]
      } else {
        initLogger.log('HubSpotConversations available, loading widget directly')
        loadHubSpotWidget()
      }

      // Verify widget loaded correctly after a delay
      setTimeout(() => {
        const status = getHubSpotWidgetStatus()
        initLogger.log('Widget load status after delay:', status)

        // If widget failed to load, try one more time
        if (!status?.loaded) {
          initLogger.warn('Widget not loaded after initial attempt, trying again')
          loadHubSpotWidget()
        }
      }, 3000)
    } catch (error: unknown) {
      initLogger.error('Unexpected error during HubSpot initialization:', error)
    } finally {
      initializationState.current.initializationInProgress = false
    }
  }, [account, apiClient, hubSpotIdentificationToken, fetchIdentificationToken, locale])

  // Handle script loading event
  useEffect(() => {
    const effectLogger = createLogger('scriptLoadEffect')

    const handleScriptLoaded = () => {
      effectLogger.log('HubSpot script loaded event received')
      initializationState.current.scriptLoaded = true

      // Initialize if auth is available
      if (auth?.email && !initializationState.current.initAttempted) {
        effectLogger.log('Initializing HubSpot after script load with auth', { email: auth.email })
        initializeHubSpot()
      }
    }

    // Check if script is already loaded
    if (isHubSpotLoaded() && !initializationState.current.scriptLoaded) {
      effectLogger.log('HubSpot already loaded on mount')
      initializationState.current.scriptLoaded = true

      if (auth?.email && !initializationState.current.initAttempted) {
        effectLogger.log('Initializing HubSpot on mount with auth', { email: auth.email })
        initializeHubSpot()
      }
    }

    // Listen for script load event
    window.addEventListener(HUBSPOT_LOADED_EVENT, handleScriptLoaded)

    return () => {
      window.removeEventListener(HUBSPOT_LOADED_EVENT, handleScriptLoaded)
    }
  }, [auth?.email, initializeHubSpot])

  // Handle auth changes
  useEffect(() => {
    const authLogger = createLogger('authEffect')

    authLogger.log('Auth or locale changed', {
      hasAuth: !!auth,
      email: auth?.email,
      locale,
    })

    if (!auth?.email) {
      authLogger.log('No auth email, resetting widget')
      resetHubSpotWidget()
      return
    }

    // Only proceed if script is loaded
    if (!initializationState.current.scriptLoaded) {
      authLogger.log('Script not loaded yet, will initialize when script loads')
      return
    }

    authLogger.log('Initializing HubSpot with auth', { email: auth.email })
    initializeHubSpot()
  }, [auth?.email, initializeHubSpot, locale])

  // Handle debugging in production
  useEffect(() => {
    const debugLogger = createLogger('debug')

    // Log initialization state every 30 seconds in production
    const debugInterval = setInterval(() => {
      const hubspotStatus = getHubSpotWidgetStatus()

      debugLogger.log('HubSpot debug info', {
        isLoaded: isHubSpotLoaded(),
        widgetStatus: hubspotStatus,
        initState: { ...initializationState.current },
        hasToken: !!hubSpotIdentificationToken,
        tokenLength: hubSpotIdentificationToken?.length,
        hasAccount: !!account,
        hasAuth: !!auth,
        authEmail: auth?.email,
        locale,
      })
    }, 30000)

    return () => {
      clearInterval(debugInterval)
    }
  }, [account, auth, hubSpotIdentificationToken, locale])
}
